// One-click "E-mail the guest this chat + our offer" panel for the reply box.
import React, { useState } from 'react';
import { ExternalLink, Loader2, Mail, X } from 'lucide-react';
import { API_URL, apiCall, getToken } from './_shared';

const TranscriptEmail = ({ sessionId, guestEmail, draft, onDone }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [offer, setOffer] = useState('');
  const [includeBot, setIncludeBot] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const toggle = () => { if (!open) setOffer(draft || ''); setOpen((v) => !v); setResult(null); setErr(''); };
  const preview = async () => {
    const token = getToken() || '';
    const csrf = document.cookie.match(/(?:^|; )csrf_token=([^;]*)/)?.[1] || '';
    const r = await fetch(`${API_URL}/api/admin/chat-sessions/${sessionId}/email-transcript/preview.html?offer=${encodeURIComponent(offer)}`, { credentials: 'include', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(csrf ? { 'X-CSRF-Token': csrf } : {}) } });
    window.open(URL.createObjectURL(await r.blob()), '_blank');
  };
  const send = async () => {
    setBusy(true); setErr('');
    try {
      const r = await apiCall(`/api/admin/chat-sessions/${sessionId}/email-transcript`, { method: 'POST', body: JSON.stringify({ email: email.trim() || null, offer: offer.trim(), include_bot: includeBot }) });
      setResult(r);
      onDone?.();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="inline-flex flex-col items-end gap-1">
      <button type="button" onClick={toggle} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 border text-sky-700 border-sky-300 hover:bg-sky-50" title="E-mail the guest a copy of this chat with the offer discussed" data-testid="conversation-email-transcript">
        <Mail className="w-3.5 h-3.5" /> E-mail transcript
      </button>
      {open && (
        <div className="w-full sm:w-[440px] bg-white border border-sky-300 rounded-lg p-3 shadow-lg space-y-2 text-left" data-testid="transcript-email-panel">
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-sky-800">E-mail this chat to the guest</span>
            <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-red-600" aria-label="Close" data-testid="transcript-email-close"><X className="w-4 h-4" /></button>
          </div>
          {guestEmail ? (
            <div className="text-xs text-slate-600">To: <b data-testid="transcript-email-to">{guestEmail}</b></div>
          ) : (
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Guest e-mail address" className="w-full border rounded px-2 py-1 text-xs" data-testid="transcript-email-input" />
          )}
          <textarea value={offer} onChange={(e) => setOffer(e.target.value)} rows={3} placeholder="The offer discussed (e.g. Sea-view double 10–12 Nov, €120/night incl. breakfast, free cancellation until 8 Nov)…" className="w-full border rounded px-2 py-1 text-xs" data-testid="transcript-email-offer" />
          <label className="text-[11px] text-slate-600 inline-flex items-center gap-1"><input type="checkbox" checked={includeBot} onChange={(e) => setIncludeBot(e.target.checked)} data-testid="transcript-email-include-bot" /> include the concierge (AI) answers</label>
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={preview} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="transcript-email-preview"><ExternalLink className="w-3 h-3" /> Preview</button>
            <button type="button" onClick={send} disabled={busy || (!guestEmail && !email.trim())} className="text-xs bg-sky-600 hover:bg-sky-700 text-white font-semibold rounded px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1" data-testid="transcript-email-send">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Send e-mail
            </button>
          </div>
          {err && <div className="text-xs text-red-600" data-testid="transcript-email-error">{err}</div>}
          {result && (
            <div className={`text-[11px] ${result.ok ? 'text-emerald-800' : 'text-amber-800'}`} data-testid="transcript-email-result">
              {result.ok ? `Sent to ${result.to}.` : `Not sent to ${result.to}: ${result.skipped_reason || result.error} (e-mail delivery not configured).`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TranscriptEmail;
